import React from 'react';
import { Box, Typography, TextField, Grid, Button, InputAdornment, Tabs, Tab } from '@mui/material';
import type { EstimateFull } from '../../services/estimateService';
import { updateEstimate } from '../../services/estimateService';
import { useNotification } from '../layouts/Layout';
import { FieldLabel, multilineFieldSx, sectionTitleSx, textFieldSx } from './easyEstimateFormFields';

function subTabA11y(index: number) {
  return {
    id: `easy-estimate-karta-info-subtab-${index}`,
    'aria-controls': `easy-estimate-karta-info-panel-${index}`,
  };
}

function toDateInput(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return String(iso).slice(0, 10);
  } catch {
    return '';
  }
}

function parseNum(s: string): number {
  const n = parseFloat(String(s).replace(',', '.').replace(/%/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

export type EasyEstimateKartaInfoTabProps = {
  estimate: EstimateFull;
  onSaved: () => void;
  /** Nagłówek + nazwa obiektu + branża (zakładka Ogólne) — np. dolny pasek EasyEstimate. */
  onOgolneSummaryChange?: (v: { headerName: string; objectName: string; branza: string }) => void;
  /** Roboczogodzina (zł) — np. synchronizacja z arkuszem KNR w kosztorysie. */
  onRgChange?: (rg: string) => void;
};

type FormState = {
  headerName: string;
  preparedAt: string;
  objectName: string;
  estimateLocation: string;
  branza: string;
  client: string;
  clientAddress: string;
  contractorName: string;
  contractorAddress: string;
  preparer: string;
  reviewer: string;
  kp: string;
  kz: string;
  z: string;
  vat: string;
  rg: string;
};

function mapEstimateToForm(e: EstimateFull): FormState {
  return {
    headerName: e.name || '',
    preparedAt: toDateInput(e.prepared_at),
    objectName: e.object_name || '',
    estimateLocation: e.estimate_location || '',
    branza: e.branza || '',
    client: e.client || '',
    clientAddress: e.client_address || '',
    contractorName: e.contractor_name || '',
    contractorAddress: e.contractor_address || '',
    preparer: e.preparer || '',
    reviewer: e.reviewer || '',
    kp: String(e.markup_kp ?? ''),
    kz: String(e.markup_kz ?? ''),
    z: String(e.markup_z ?? ''),
    vat: String(e.vat ?? ''),
    rg: String(e.rg ?? ''),
  };
}

const EasyEstimateKartaInfoTab: React.FC<EasyEstimateKartaInfoTabProps> = ({
  estimate,
  onSaved,
  onOgolneSummaryChange,
  onRgChange,
}) => {
  const { showNotification } = useNotification();
  const [form, setForm] = React.useState<FormState>(() => mapEstimateToForm(estimate));
  const [saving, setSaving] = React.useState(false);
  const [innerTab, setInnerTab] = React.useState(0);
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    setForm(mapEstimateToForm(estimate));
  }, [estimate]);

  React.useEffect(() => {
    onOgolneSummaryChange?.({
      headerName: form.headerName,
      objectName: form.objectName,
      branza: form.branza,
    });
  }, [form.headerName, form.objectName, form.branza, onOgolneSummaryChange]);

  React.useEffect(() => {
    onRgChange?.(form.rg);
  }, [form.rg, onRgChange]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async () => {
    const name = form.headerName.trim();
    const object_name = form.objectName.trim();
    const client = form.client.trim();
    if (!name || !object_name || !client) {
      setShowErrors(true);
      showNotification('Uzupełnij wymagane pola: nagłówek, nazwa obiektu, zamawiający.', 'error');
      setInnerTab(object_name ? (client ? 0 : 1) : 0);
      return;
    }
    setShowErrors(false);
    setSaving(true);
    try {
      await updateEstimate(estimate.id, {
        name,
        object_name,
        client,
        prepared_at: form.preparedAt,
        estimate_location: form.estimateLocation.trim() || undefined,
        branza: form.branza.trim().slice(0, 100) || null,
        client_address: form.clientAddress.trim() || undefined,
        contractor_name: form.contractorName.trim() || undefined,
        contractor_address: form.contractorAddress.trim() || undefined,
        preparer: form.preparer.trim() || '—',
        reviewer: form.reviewer.trim() || undefined,
        markup_kp: parseNum(form.kp),
        markup_kz: parseNum(form.kz),
        markup_z: parseNum(form.z),
        vat: parseNum(form.vat),
        rg: parseNum(form.rg),
      });
      showNotification('Zapisano dane kosztorysu.', 'success');
      onSaved();
    } catch {
      showNotification('Nie udało się zapisać zmian.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const idLabel = estimate.estimate_number || String(estimate.id);

  const sectionTitle = (label: string) => <Typography sx={sectionTitleSx}>{label}</Typography>;

  const dateFieldSx = {
    ...textFieldSx,
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: form.preparedAt ? '#2D2D32' : '#B9B9B9',
    },
  } as const;

  /** Wyrównanie z dolnym paskiem zakładek (sidebar + padding layoutu). */
  const pageGutterSx = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    pl: { xs: 2, sm: 'calc(40px + 12px + 24px + 16px)' },
    pr: { xs: 2, sm: 3 },
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        pt: { xs: 2, sm: 3 },
        pb: 0,
        ...pageGutterSx,
      }}
    >
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
          Karta Info
        </Typography>
        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#5F6368', mt: 0.5 }}>{idLabel}</Typography>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          position: 'relative',
          pb: 0,
          minHeight: 48,
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            bgcolor: '#EDEDED',
          },
        }}
      >
        <Tabs
          value={innerTab}
          onChange={(_, v) => setInnerTab(v)}
          variant="standard"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            position: 'relative',
            '& .MuiTabs-flexContainer': { justifyContent: 'flex-start', gap: { xs: 2, sm: 4 } },
          }}
          TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
        >
          {(['Ogólne', 'Dane', 'Narzuty'] as const).map((label, i) => (
            <Tab
              key={label}
              label={label}
              {...subTabA11y(i)}
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                textTransform: 'none',
                color: '#020242',
                minWidth: 'auto',
                px: 0,
                '&.Mui-selected': { fontWeight: 500, color: '#020242' },
              }}
            />
          ))}
        </Tabs>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            alignSelf: { xs: 'flex-end', sm: 'center' },
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#4040DB',
            '&:hover': { backgroundColor: '#1F1FB0' },
            minWidth: 160,
            mb: { xs: 0, sm: 0.5 },
          }}
        >
          {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: { xs: 0, sm: 1 },
          pb: 2,
        }}
      >
        {innerTab === 0 && (
          <Grid container rowSpacing={1.5} columnSpacing={3} role="tabpanel" id="easy-estimate-karta-info-panel-0">
            <Grid item xs={12}>
              {sectionTitle('Ogólne')}
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Nagłówek kosztorysu" required />
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                value={form.headerName}
                onChange={(e) => update('headerName', e.target.value)}
                sx={textFieldSx}
                error={showErrors && !form.headerName.trim()}
                helperText={showErrors && !form.headerName.trim() ? 'To pole jest wymagane' : undefined}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FieldLabel text="Data opracowania" />
              <TextField
                type="date"
                fullWidth
                variant="outlined"
                size="small"
                value={form.preparedAt}
                onChange={(e) => update('preparedAt', e.target.value)}
                sx={dateFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Nazwa obiektu (robót)" required />
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                value={form.objectName}
                onChange={(e) => update('objectName', e.target.value)}
                sx={textFieldSx}
                error={showErrors && !form.objectName.trim()}
                helperText={showErrors && !form.objectName.trim() ? 'To pole jest wymagane' : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Lokalizacja" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.estimateLocation}
                onChange={(e) => update('estimateLocation', e.target.value)}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Branża" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.branza}
                onChange={(e) => update('branza', e.target.value.slice(0, 100))}
                inputProps={{ maxLength: 100 }}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        )}

        {innerTab === 1 && (
          <Grid container rowSpacing={1.5} columnSpacing={3} role="tabpanel" id="easy-estimate-karta-info-panel-1">
            <Grid item xs={12}>
              {sectionTitle('Dane')}
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Zamawiający" required />
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                value={form.client}
                onChange={(e) => update('client', e.target.value)}
                sx={textFieldSx}
                error={showErrors && !form.client.trim()}
                helperText={showErrors && !form.client.trim() ? 'To pole jest wymagane' : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Adres zamawiającego" />
              <TextField
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                size="small"
                value={form.clientAddress}
                onChange={(e) => update('clientAddress', e.target.value)}
                sx={multilineFieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Wykonawca" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.contractorName}
                onChange={(e) => update('contractorName', e.target.value)}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Adres wykonawcy" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.contractorAddress}
                onChange={(e) => update('contractorAddress', e.target.value)}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Kosztorysant" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.preparer}
                onChange={(e) => update('preparer', e.target.value)}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Sprawdzający" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.reviewer}
                onChange={(e) => update('reviewer', e.target.value)}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        )}

        {innerTab === 2 && (
          <Grid container rowSpacing={1.5} columnSpacing={3} role="tabpanel" id="easy-estimate-karta-info-panel-2">
            <Grid item xs={12}>
              {sectionTitle('Narzuty')}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FieldLabel text="Koszty pośrednie (Kp)" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.kp}
                onChange={(e) => update('kp', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FieldLabel text="Koszty zakupu (Kz)" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.kz}
                onChange={(e) => update('kz', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FieldLabel text="Zysk (Z)" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.z}
                onChange={(e) => update('z', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FieldLabel text="VAT" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.vat}
                onChange={(e) => update('vat', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FieldLabel text="Roboczogodzina" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.rg}
                onChange={(e) => update('rg', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default EasyEstimateKartaInfoTab;
